import { ContentContainer, Spinner } from '@ahc-digital/component-library';
import {
  ComponentRendering,
  constants,
  GetServerSideComponentProps,
  LayoutServiceData,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Script from 'next/script';
import { useState } from 'react';
import { sparkleUtils } from 'src/shared/sparkle-utils';
import { useSparkleApi } from 'src/shared/SparkleApi.service';
import styles from './I03SparkleProviderProfile.module.scss';
import { I03SparkleProviderProfileProps } from './I03SparkleProviderProfile.types';
import { translationClasses } from 'src/shared/TranslationClasses';
import { ImportedMetaData } from 'src/shared/ImportedMetaData';
import { ImportedDataProviderFactory } from 'lib/imported-data';
import { GetServerSidePropsContext } from 'next';
import { ImportedData, ImportedDataType } from 'lib/imported-data/ImportedData.types';

export const Default = (props?: I03SparkleProviderProfileProps): JSX.Element => {
  const sparkleApi = useSparkleApi();
  const { sitecoreContext } = useSitecoreContext();
  const [isError, setIsError] = useState(false);

  const isWildcardPage = sitecoreContext.route?.name === '*';

  const applyMetadata = async (providerSlug: string) => {
    const { isSuccess, textContent: metaDataHtml } = await sparkleApi.fetchProviderMetadata(
      providerSlug
    );

    if (!isSuccess) {
      setIsError(true);
    }

    const doc = new DOMParser().parseFromString(metaDataHtml, 'text/html');

    // Replace metadata tags
    [
      'title',
      'meta[name="description"]',
      'meta[name="keywords"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:locale"]',
      'meta[property="og:site_name"]',
      'meta[property="og:type"]',
      'meta[property="og:image"]',
      'meta[property="og:url"]',
      'link[rel="canonical"]',
      'meta[property="og:profile:first_name"]',
      'meta[property="og:profile:last_name"]',
      'meta[property="og:profile:gender"]',
      'meta[name="breadcrumb"]',
      'script[type="application/ld+json"]',
    ].forEach((selector: string) => {
      const pageNode = document.querySelector(selector);
      const snippetNode = doc.querySelector(selector);
      if (pageNode && snippetNode) {
        pageNode.replaceWith(snippetNode.cloneNode(true));
        snippetNode.remove();
      }
    });

    // Replace breadcrumb page title
    const breadcrumbPage = document.querySelector('[data-testid="breadcrumb-page"]');
    const newTitle = document.querySelector('meta[name="breadcrumb"]');
    if (newTitle && breadcrumbPage) {
      breadcrumbPage.textContent = newTitle.getAttribute('content');
    }

    const head = document.head || document.getElementsByTagName('head')[0];
    doc.head.childNodes.forEach((node) => {
      head.appendChild(node.cloneNode(true));
    });
  };

  const applyContent = async (providerSlug: string) => {
    const providerContentDiv = document.getElementById('provider-content');
    if (!providerContentDiv) return;

    const { isSuccess, textContent: htmlContent } = await sparkleApi.fetchProviderContent(
      providerSlug
    );

    if (!isSuccess) {
      setIsError(true);
      return;
    }

    sparkleUtils.applyHtmlSnippet(htmlContent, providerContentDiv);
  };

  return (
    <>
      <ImportedMetaData data={props?.importedData}></ImportedMetaData>
      <Script
        src="https://code.jquery.com/jquery-3.7.1.min.js"
        integrity="sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo="
        crossOrigin="anonymous"
        onError={() => setIsError(true)}
        onReady={() => {
          const providerSlug = sparkleUtils.getSlug(window.location.href);
          if (!providerSlug) return;

          if (isWildcardPage) {
            applyMetadata(providerSlug);
          }
          applyContent(providerSlug);
        }}
      />
      <div id="provider-content" className={styles['provider-content']}>
        {isError ? (
          <></>
        ) : (
          <ContentContainer
            id={props?.params?.AnchorLinkTargetName}
            className={translationClasses(props?.params)}
            spacing="40-24"
          >
            <Spinner />
          </ContentContainer>
        )}
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideComponentProps = async (
  _rendering: ComponentRendering,
  _layoutData: LayoutServiceData,
  context: GetServerSidePropsContext
) => {
  if (process.env.JSS_MODE === constants.JSS_MODE.DISCONNECTED) {
    return { importedData: {} as ImportedData };
  }
  const slug = sparkleUtils.getSlug(context.req.url ?? '');
  if (sparkleUtils.isEmptyOrWhiteSpace(slug)) return;

  const provider = ImportedDataProviderFactory.create();
  const result = await provider.getDataFor(slug, ImportedDataType.SparkleProvider);
  return { importedData: result };
};
